/**
 * useDadosDeCobranca — lógica da modal "Dados para o pagamento" (pacote e domínio).
 * Planos: docs/PLANO-DOCUMENTO-CPF-CNPJ-E-COBRANCA.md (dados), docs/PLANO-CARTAO-PACOTES.md (meio) e
 * docs/PLANO-MEIOS-DE-PAGAMENTO-NO-BANCO.md (FORMAS ligadas no painel do Dashboard).
 *
 * Uso:
 *   const cobranca = useDadosDeCobranca();
 *   cobranca.abrir(async ({ document, billingName, method, optionCode }) => {
 *     try { ...compra...; return { ok: true }; }
 *     catch (e) { return { ok: false, erro: 'mensagem' }; }   // ou { ok: false, fechar: true }
 *   }, { obterMetodos: () => billingService.getPaymentOptions('pack', preco) });   // sem isto: só PIX
 *   <DadosDeCobrancaDialog vm={cobranca.vm} actions={cobranca.actions} />
 *
 * `obterMetodos` pode devolver:
 *  - { options: [...] } (formas do painel): UM item do radio por forma, na ordem do painel (a primeira vem marcada);
 *    rótulo, aviso e botão vêm do servidor. A forma pode ter vários meios. A compra recebe `optionCode` (a forma) e
 *    `method` (o primeiro meio dela — campo antigo, a API usa a forma).
 *  - lista de meios (['Pix','CreditCard']) ou { methods } sem options (API antiga): textos fixos de MEIOS_DE_PAGAMENTO,
 *    PIX marcado se houver.
 *  - { options: [] }: nenhuma forma ligada — a modal avisa e não confirma.
 *
 * Ciclo:
 *  - abrir: busca EM PARALELO os dados salvos (GET /users/documents — sugestão já resolvida pela API:
 *    cobrança salva → senão conta) e as formas. Falhou um deles → em branco / só PIX; nunca trava a compra.
 *  - confirmar: valida no front (dígito verificador, nome) e só então chama a compra com o documento
 *    LIMPO e a forma escolhida. Quem grava a cobrança é a API, DEPOIS que o pedido existe.
 */
import { useState, useCallback, useRef } from 'react';
import usersService from '../services/usersService';
import { mascararDocumento, validarDocumento } from '../utils/documento';
import {
  DADOS_DE_COBRANCA_FECHADO as FECHADO,
  MEIOS_DE_PAGAMENTO,
} from '../components/billing/dadosDeCobranca.vm';

const SO_PIX = ['Pix'];
export const SEM_FORMA_DE_PAGAMENTO = 'Nenhuma forma de pagamento está disponível agora. Tente de novo em alguns minutos.';

/**
 * Resposta de `obterMetodos` → opções completas (o que a View vê é só { id, label }).
 * `fluxo` = como o pagamento é montado ('on_screen' | 'redirect' | 'embedded'); hoje a tela segue pela forma da
 * resposta do pedido (pix → QR, checkoutUrl → página). Fica aqui para o dia do formulário dentro da modal, que
 * precisa ser desenhado ANTES de confirmar.
 * @returns {{ id: string, label: string, hint: string, confirmLabel: string, meios: string[], optionCode: string|null, fluxo: string|null }[]}
 */
export function montarOpcoes(resposta) {
  const formas = Array.isArray(resposta?.options) ? resposta.options : null;
  if (formas) {
    return formas
      .filter((f, i, todas) => f?.code && f?.label && todas.findIndex((x) => x?.code === f.code) === i)
      .map((f) => {
        const meios = Array.isArray(f.methods) ? f.methods : [];
        const padrao = MEIOS_DE_PAGAMENTO[meios[0]] ?? MEIOS_DE_PAGAMENTO.Pix;
        return {
          id: f.code,
          label: f.label,
          hint: f.hint || padrao.hint,
          confirmLabel: f.confirmLabel || padrao.confirmLabel,
          meios,
          optionCode: f.code,
          fluxo: f.flow ?? null,
        };
      });
  }

  const ids = Array.isArray(resposta) ? resposta : (Array.isArray(resposta?.methods) ? resposta.methods : []);
  const conhecidos = ids.filter((id, i, todos) => MEIOS_DE_PAGAMENTO[id] && todos.indexOf(id) === i);
  return (conhecidos.length ? conhecidos : SO_PIX).map((id) => ({
    id, label: MEIOS_DE_PAGAMENTO[id].label, hint: MEIOS_DE_PAGAMENTO[id].hint,
    confirmLabel: MEIOS_DE_PAGAMENTO[id].confirmLabel, meios: [id], optionCode: null, fluxo: null,
  }));
}

/**
 * Qual opção vem marcada: com formas do painel, a PRIMEIRA da ordem (o painel decide); com meios (API antiga), PIX
 * se houver. Sem opção nenhuma → undefined.
 */
export function opcaoPadrao(resposta, lista) {
  if (Array.isArray(resposta?.options)) return lista[0]?.id;
  return lista.some((m) => m.id === 'Pix') ? 'Pix' : lista[0]?.id;
}

/** Texto acima do botão e rótulo do botão acompanham a forma escolhida. */
function comMetodo(estado, id, opcoes) {
  const opcao = opcoes.find((o) => o.id === id);
  if (!opcao) return { ...estado, method: '', methodHint: '', confirmLabel: MEIOS_DE_PAGAMENTO.Pix.confirmLabel };
  return { ...estado, method: id, methodHint: opcao.hint, confirmLabel: opcao.confirmLabel };
}

export default function useDadosDeCobranca() {
  const [estado, setEstado] = useState(FECHADO);
  const aoConfirmar = useRef(null);
  // Opções completas da abertura atual (aviso, botão, meios, código da forma) — a View só recebe { id, label }.
  const opcoes = useRef([]);
  // Cada abertura tem um número: a resposta de uma busca antiga (abriu, fechou, abriu de novo)
  // não pode preencher a modal da abertura nova.
  const abertura = useRef(0);

  const abrir = useCallback(async (onConfirm, { obterMetodos } = {}) => {
    const minha = ++abertura.current;
    aoConfirmar.current = onConfirm;
    opcoes.current = [];
    setEstado({ ...FECHADO, open: true, loading: true });

    const [dados, resposta] = await Promise.all([
      usersService.getDocuments().catch(() => null),          // sem dados salvos a pessoa digita
      obterMetodos ? obterMetodos().catch(() => SO_PIX) : Promise.resolve(SO_PIX),
    ]);
    if (minha !== abertura.current) return;

    const lista = montarOpcoes(resposta);
    opcoes.current = lista;
    const padrao = opcaoPadrao(resposta, lista);
    setEstado((e) => (e.open
      ? comMetodo({
        ...e,
        loading: false,
        name: dados?.billingSuggestion?.billingName ?? '',
        document: mascararDocumento(dados?.billingSuggestion?.billingDocument ?? ''),
        methods: lista.map(({ id, label }) => ({ id, label })),
        formError: lista.length ? null : SEM_FORMA_DE_PAGAMENTO,
      }, padrao, lista)
      : e));
  }, []);

  const onChangeName = useCallback((valor) => {
    setEstado((e) => ({ ...e, name: valor, nameError: null, formError: e.methods.length ? null : e.formError }));
  }, []);

  const onChangeDocument = useCallback((valor) => {
    setEstado((e) => ({ ...e, document: mascararDocumento(valor), documentError: null, formError: e.methods.length ? null : e.formError }));
  }, []);

  const onChangeMethod = useCallback((id) => {
    setEstado((e) => (e.busy || !e.methods.some((m) => m.id === id) ? e : comMetodo({ ...e, formError: null }, id, opcoes.current)));
  }, []);

  const onCancel = useCallback(() => {
    setEstado((e) => (e.busy ? e : FECHADO));
  }, []);

  const onConfirm = useCallback(async () => {
    const atual = estado;
    if (atual.busy || atual.loading) return;

    const escolhida = opcoes.current.find((o) => o.id === atual.method);
    if (!escolhida) {
      setEstado((e) => ({ ...e, formError: SEM_FORMA_DE_PAGAMENTO }));
      return;
    }

    const documento = validarDocumento(atual.document);
    const nome = (atual.name || '').trim();
    const nameError = nome ? null : 'Informe o nome ou a razão social.';
    if (!documento.valido || nameError) {
      setEstado((e) => ({ ...e, documentError: documento.erro, nameError }));
      return;
    }

    setEstado((e) => ({ ...e, busy: true, formError: null }));
    let resultado;
    try {
      resultado = await aoConfirmar.current?.({
        document: documento.limpo,
        billingName: nome,
        method: escolhida.meios[0] ?? 'Pix',
        optionCode: escolhida.optionCode ?? undefined,
      });
    } catch {
      resultado = { ok: false, erro: 'Não foi possível iniciar o pagamento. Tente novamente.' };
    }

    if (resultado?.ok || resultado?.fechar) {
      setEstado(FECHADO);
      return;
    }
    setEstado((e) => ({
      ...e,
      busy: false,
      formError: resultado?.erro || 'Não foi possível iniciar o pagamento. Tente novamente.',
    }));
  }, [estado]);

  return {
    abrir,
    vm: estado,
    actions: { onChangeName, onChangeDocument, onChangeMethod, onConfirm, onCancel },
  };
}
